/**
 * AboutThisUserPanel — other-profile right-rail card showing "About this user" rows
 * + topic-pill chips.
 *
 * AboutRow.icon is a legacy SVG-markup string — NOT an IconName.
 * We use a fixed neutral <Icon name="check"> for every row instead.
 * (Same caveat as AboutPanel.)
 *
 * Pure component — no hooks.
 */
import styled from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import { Icon } from '@/icons';
import type { User } from '@/core/models';

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const RowList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  line-height: 1.4;
`;

const IconWrap = styled.span`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${({ theme }) => theme.color.textMuted};
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.color.brandFrom};
  background: ${({ theme }) => theme.color.brandFrom}1f;
  border: 1px solid transparent;
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ──────────────────────────────────────────────────────────────── */

export interface AboutThisUserPanelProps {
  user: User;
}

export function AboutThisUserPanel({ user }: AboutThisUserPanelProps) {
  const rows = user.aboutThisUser ?? [];
  const pills = user.topicPills ?? [];
  const isEmpty = rows.length === 0 && pills.length === 0;

  return (
    <Panel>
      <SectionLabel as="h2">About this user</SectionLabel>

      {isEmpty ? (
        <EmptyState>No details available.</EmptyState>
      ) : (
        <>
          {rows.length > 0 && (
            <RowList>
              {rows.map((row, i) => (
                <Row key={i}>
                  <IconWrap>
                    {/* row.icon is a legacy SVG string — always use a fixed neutral icon */}
                    <Icon name="check" size={14} />
                  </IconWrap>
                  {row.text}
                </Row>
              ))}
            </RowList>
          )}

          {pills.length > 0 && (
            <PillRow>
              {pills.map((pill) => (
                <Pill key={pill}>{pill}</Pill>
              ))}
            </PillRow>
          )}
        </>
      )}
    </Panel>
  );
}
